export const authors = {
  hxhippy: {
    id: 'hxhippy',
    name: 'Brian Gagne',
    title: 'Visionary CTO & CIO | AI & Cybersecurity Strategist',
    bio: 'CTO/COO at Kief Studio, CIO at TRaViS. Passionate about building secure, performant software with Rust.',
    image: '/assets/images/hxhippy.jpeg',
    social: {
      linkedin: 'https://linkedin.com/in/hxhippy',
      twitter: 'https://x.com/HxHippy',
      github: 'https://github.com/KiefStudioMA',
    },
  },
  kief: {
    id: 'kief',
    name: 'Kief Studio Team',
    title: 'AI Integration & Technology Consulting',
    bio: 'Leading AI integration and technology consulting services.',
    image: '/img/KS-Social.png',
    url: 'https://kief.studio',
  },
  travis: {
    id: 'travis',
    name: 'TRaViS Team',
    title: 'AI-Powered EASM & Threat Intelligence',
    bio: 'Advanced threat intelligence and external attack surface management.',
    image: '/assets/images/travis-text-dark.png',
    url: 'https://travisasm.com',
  },
};

export const blogPosts = [
  {
    slug: 'pwgen-rust-snap-store-launch',
    title: 'PWGen-Rust Now Available on Snap Store!',
    date: new Date('2025-07-08'),
    authors: ['hxhippy'],
    tags: ['announcement', 'linux', 'snap', 'distribution', 'installation', 'ubuntu', 'release'],
    excerpt: 'Great news for Linux users! PWGen-Rust is now officially available on the Snap Store. Install with a single command: sudo snap install pwgen-rust',
    metaDescription: 'PWGen-Rust is now available on Snap Store! Install the secure password manager on Linux with one command: sudo snap install pwgen-rust. Get started today.',
    keywords: ['PWGen-Rust', 'Snap Store', 'Linux installation', 'Ubuntu', 'snap package', 'password manager Linux', 'secure password storage'],
    ogImage: '/img/41.webp',
    socialImage: '/img/pwgen-social-card.png',
    canonicalUrl: 'https://pwgenrust.dev/blog/pwgen-rust-snap-store-launch',
    readingTime: 3,
    content: `# PWGen-Rust Now Available on Snap Store! 🎉

![PWGen-Rust Snap Store Launch](/img/41.webp)

We're thrilled to announce that **PWGen-Rust is now officially available on the Snap Store**! This means Linux users can now install our secure password manager with a single command.

## 🚀 Quick Installation

Installing PWGen-Rust on Linux has never been easier:

\`\`\`bash
sudo snap install pwgen-rust
\`\`\`

That's it! The snap package includes all dependencies and will automatically stay updated with the latest security patches and features.

## 📦 What's Included

The snap package provides:

- **Full PWGen-Rust functionality** - All features available in other distributions
- **Automatic updates** - Stay secure with the latest patches
- **Sandboxed security** - Additional isolation for enhanced protection
- **Cross-distribution support** - Works on Ubuntu, Fedora, Debian, and more

## 🐧 Why Snap?

We chose to distribute through Snap Store because it:

- **Simplifies installation** - No more dependency hunting
- **Works everywhere** - One package for all Linux distributions
- **Auto-updates** - Security patches applied automatically
- **Maintains security** - Snap's confinement adds an extra security layer

## 🔧 Other Installation Options

While snap is now our recommended installation method for Linux, we still offer:

- **AppImage** - Portable, no installation required
- **Debian packages** - For .deb-based systems
- **Source compilation** - Via cargo for maximum control

## 🙏 Thank You

A huge thank you to our community for your patience during the Snap Store review process. Your support and feedback have been invaluable in making PWGen-Rust better.

## Get Started Today

Ready to secure your passwords? Install PWGen-Rust now:

\`\`\`bash
sudo snap install pwgen-rust
pwgen-rust --help
\`\`\`

[![Get it from the Snap Store](https://snapcraft.io/en/dark/install.svg)](https://snapcraft.io/pwgen-rust)

For more information, visit our [download page](/download) or check out the [documentation](/docs).

---

*Have questions or feedback? Join the discussion on [GitHub](https://github.com/KiefStudioMA/PWGen-Rust) or reach out on [Twitter](https://x.com/Kief_ma).*`
  },
  {
    slug: 'pwgen-1.0-release',
    title: 'PWGen-Rust 1.0 - The Future of Password Management is Here',
    date: new Date('2024-01-15'),
    authors: ['kief'],
    tags: ['release', 'security', 'rust', 'password-manager', 'encryption', 'zero-knowledge', 'cybersecurity', 'software-release'],
    excerpt: 'After months of development, we\'re thrilled to announce the official 1.0 release of PWGen-Rust - a revolutionary password manager built from the ground up in Rust with military-grade security.',
    metaDescription: 'PWGen-Rust 1.0 is now available! Revolutionary password manager built in Rust with AES-256 encryption, zero-knowledge architecture, and comprehensive secrets management. Download now.',
    keywords: ['PWGen-Rust', 'password manager', 'Rust security', 'zero-knowledge encryption', 'AES-256', 'Argon2', 'password security', 'secrets management', 'cybersecurity software'],
    ogImage: '/img/1.webp',
    socialImage: '/img/pwgen-social-card.png',
    canonicalUrl: 'https://pwgenrust.dev/blog/pwgen-1.0-release',
    readingTime: 8,
    content: `# PWGen-Rust 1.0 - The Future of Password Management is Here 🚀

![PWGen-Rust Release Header](/img/1.webp)

After months of development, we're thrilled to announce the official **1.0 release** of PWGen-Rust - a revolutionary password manager built from the ground up in **Rust** 🦀.

## What Makes PWGen-Rust Different?

### 🔒 Zero-Knowledge Architecture
Your master password **never leaves your device**. All encryption and decryption happens locally, giving you complete control over your sensitive data.

### ⚡ Built in Rust for Performance & Safety
- **Memory Safety**: Prevents buffer overflows and memory vulnerabilities
- **Performance**: Native speed with minimal resource usage
- **Reliability**: No crashes from memory management issues

### 🛡️ Military-Grade Security
- **AES-256-GCM Encryption**: Industry-standard symmetric encryption
- **Argon2 Key Derivation**: Secure password hashing with memory hardness
- **SHA-256 Fingerprints**: Secure data integrity verification

## Comprehensive Secrets Management

PWGen-Rust goes beyond traditional password managers:

- **🔑 Passwords**: Traditional password storage with auto-generation
- **🗝️ API Keys**: Secure storage for development and automation credentials
- **🔐 SSH Keys**: Encrypted storage of private keys with metadata
- **📄 Documents**: Secure file storage for certificates and licenses
- **⚙️ Environment Variables**: Configuration secrets for development

## Enterprise Features

### 👥 Team Collaboration
- Role-based access control
- Shared vaults for teams
- Audit logging for compliance

### 🔧 Developer-Friendly
- **Full CLI Interface**: Perfect for automation and CI/CD
- **REST API**: Coming in Q1 2025 for custom integrations
- **Browser Extensions**: Planned for seamless web workflows

### 📊 Advanced Analytics
- Password strength analysis
- Duplicate detection
- Security breach monitoring

## Cross-Platform Excellence

Native applications for:
- **Windows**: Full desktop integration
- **macOS**: Optimized for Apple Silicon and Intel
- **Linux**: Universal compatibility across distributions

## Powered by Innovation

PWGen-Rust is proudly developed by:

- **⚡ Kief Studio**: AI Integration & Technology Consulting driving innovation in cybersecurity

## Get Started Today

\`\`\`bash
# Linux/macOS
sudo snap install pwgen-rust

# Or download from GitHub
curl -L https://github.com/KiefStudioMA/PWGen-Rust/releases/latest
\`\`\`

## What's Next?

- **Browser Extensions** (Q2 2024)
- **Mobile Apps** (Q3 2024)
- **REST API Server** (Q1 2025)
- **Enterprise SSO Integration** (Q2 2025)

Join the revolution in password management. **Your security is our mission.** 🔐

---

*Built with ❤️ and Rust 🦀 by Kief Studio*`
  },
  {
    slug: 'website-launch',
    title: 'PWGen-Rust Website Now Live!',
    date: new Date('2024-06-27'),
    authors: ['kief'],
    tags: ['announcement', 'website', 'documentation', 'launch', 'resources', 'user-guide', 'developer-docs'],
    excerpt: 'We\'re excited to announce the launch of the official PWGen-Rust website! Built with modern web technologies to provide comprehensive documentation and resources.',
    metaDescription: 'The official PWGen-Rust website is now live at pwgenrust.dev! Comprehensive documentation, user guides, API docs, and download center for the secure password manager.',
    keywords: ['PWGen-Rust website', 'password manager documentation', 'user guide', 'API documentation', 'download center', 'installation guide', 'security software'],
    ogImage: '/img/9.webp',
    socialImage: '/img/pwgen-social-card.png',
    canonicalUrl: 'https://pwgenrust.dev/blog/website-launch',
    readingTime: 4,
    content: `# PWGen-Rust Website Now Live! 🌐

![PWGen-Rust Website Launch Header](/img/9.webp)

We're excited to announce the launch of the official **PWGen-Rust website**! 🎉

## What You'll Find

Our new website at **pwgenrust.dev** provides:

### 📚 Comprehensive Documentation
- **Getting Started**: Quick installation and setup guides
- **User Manual**: Complete feature documentation
- **CLI Reference**: Full command-line interface documentation
- **API Docs**: Developer resources and integration guides

### 🎯 Features & Benefits
- Interactive feature demonstrations
- Security architecture explanations
- Performance benchmarks
- Cross-platform compatibility details

### 💾 Download Center
- Latest releases for all platforms
- Installation instructions
- System requirements
- Legacy version access

### 👨‍💻 Developer Resources
- Build instructions
- Contributing guidelines
- API documentation
- Integration examples

## Built with Modern Tech

The website is built using:
- **React**: Fast, modern user interface
- **Material-UI**: Beautiful, accessible components
- **Responsive Design**: Perfect on all devices
- **Performance Optimized**: Fast loading times

## Community Driven

This website will continue to evolve based on your feedback and needs. We've included:

- **Search functionality** for easy navigation
- **Mobile-first design** for accessibility
- **Regular updates** with new content
- **Community contributions** welcome

## Get Involved

- **🐛 Report Issues**: Help us improve the experience
- **💡 Suggest Features**: Your ideas matter
- **📝 Contribute Docs**: Share your knowledge
- **🌟 Spread the Word**: Help others discover PWGen-Rust

Visit us at **[pwgenrust.dev](https://pwgenrust.dev)** and explore everything PWGen-Rust has to offer!

---

*Thank you for being part of the PWGen-Rust community! 🚀*

*- The Kief Studio Team*`
  },
  {
    slug: 'pwgen-v1.2-optimized-release',
    title: 'PWGen-Rust v1.2 Released - 30-40% Smaller Binaries & Enhanced Security',
    date: new Date('2025-06-27'),
    authors: ['kief'],
    tags: ['release', 'optimization', 'security', 'rust', 'performance', 'binary-size', 'cryptography', 'sha-256'],
    excerpt: 'Major optimization release achieving significant binary size reductions and enhanced security improvements with SHA-256 only cryptography.',
    metaDescription: 'PWGen-Rust v1.2 is here! 30-40% smaller binaries, enhanced security with SHA-256 cryptography, flexible build system, and performance improvements. Download now.',
    keywords: ['PWGen-Rust v1.2', 'binary optimization', 'Rust performance', 'SHA-256 cryptography', 'security update', 'memory optimization', 'cross-platform builds'],
    ogImage: '/img/10.webp',
    socialImage: '/img/pwgen-social-card.png',
    canonicalUrl: 'https://pwgenrust.dev/blog/pwgen-v1.2-optimized-release',
    readingTime: 12,
    content: `# PWGen-Rust v1.2 Released - 30-40% Smaller Binaries & Enhanced Security 🚀

![PWGen-Rust v1.2 Release Header](/img/10.webp)

We're thrilled to announce **PWGen-Rust v1.2**, a major optimization release that delivers significant performance improvements and enhanced security! 🔥

## 📦 Massive Size Reductions

![PWGen v1.2 Performance Improvements](/img/7.webp)

### Binary Optimization Achievements
- **30-40% smaller binaries** across all platforms
- **Faster startup times** with optimized dependencies
- **Reduced memory footprint** for better system performance
- **Improved download speeds** for quicker installations

### How We Did It
\`\`\`toml
# Cargo.toml optimizations
[profile.release]
lto = true
codegen-units = 1
panic = "abort"
strip = true
\`\`\`

## 🔐 Enhanced Security

### Cryptographic Improvements
- **Removed MD5 hashing** entirely from the codebase
- **SHA-256 everywhere** for integrity verification
- **Enhanced secure memory handling** with automatic zeroization
- **Updated dependency security** with latest cryptographic libraries

### Security Architecture Updates
- **Improved key derivation** with optimized Argon2 parameters
- **Enhanced entropy sources** for better randomness
- **Stricter memory protection** against side-channel attacks
- **Audit trail improvements** for better security monitoring

## ⚙️ Flexible Build System

### New Build Profiles
\`\`\`bash
# Minimal build for embedded systems
cargo build --release --no-default-features --features="minimal"

# Full featured build
cargo build --release --features="full"

# Developer build with debugging
cargo build --release --features="dev-tools"
\`\`\`

### Feature Flags
- **modular-crypto**: Choose specific encryption algorithms
- **gui-optional**: CLI-only builds for servers
- **audit-logging**: Enhanced security logging
- **performance-metrics**: Runtime performance monitoring

## 🎯 Performance Benchmarks

| Operation | v1.1 | v1.2 | Improvement |
|-----------|------|------|-------------|
| Startup | 250ms | 180ms | **28% faster** |
| Encryption | 45ms | 32ms | **29% faster** |
| Search | 120ms | 85ms | **30% faster** |
| Binary Size | 15.2MB | 10.8MB | **29% smaller** |

## 🛠️ Developer Experience

### New CLI Features
\`\`\`bash
# Performance profiling
pwgen profile --operation=encrypt --iterations=1000

# Security audit
pwgen audit --comprehensive --output=json

# Benchmark comparison
pwgen benchmark --compare-with=1.1
\`\`\`

### API Improvements
- **Async/await support** for better concurrency
- **Streaming encryption** for large files
- **Batch operations** for performance
- **Enhanced error handling** with detailed contexts

## 🌐 Cross-Platform Updates

### Windows Optimizations
- **Native Windows APIs** for better integration
- **Windows Defender compatibility** improvements
- **PowerShell module** for automation

### macOS Enhancements
- **Apple Silicon optimization** with native ARM64 builds
- **Keychain integration** improvements
- **macOS security model** compliance

### Linux Improvements
- **Reduced libc dependencies** for better compatibility
- **Systemd integration** for service management
- **AppArmor/SELinux profiles** for enhanced security

## 📊 Migration Guide

### Automatic Migration
PWGen-Rust v1.2 automatically migrates your existing data:

1. **Backup creation** before migration
2. **Schema updates** to new format
3. **Security re-encryption** with enhanced algorithms
4. **Verification checks** to ensure data integrity

### Manual Steps (if needed)
\`\`\`bash
# Export from v1.1
pwgen-1.1 export --format=json --output=backup.json

# Import to v1.2
pwgen import --source=backup.json --verify
\`\`\`

## 🎉 Community Contributions

Special thanks to our contributors:
- **Security audits** from the community
- **Performance testing** across different hardware
- **Documentation improvements** and translations
- **Bug reports** and feature requests

## 🚀 What's Next?

### Q4 2025 Roadmap
- **REST API Release**: Full server-side API
- **Browser Extensions**: Chrome, Firefox, Safari
- **Mobile Applications**: iOS and Android
- **Team Management**: Advanced collaboration features

### Q1 2026 Features
- **Cloud Sync**: End-to-end encrypted synchronization
- **Advanced Analytics**: Usage insights and security metrics
- **Plugin System**: Extensible architecture
- **Enterprise SSO**: SAML and OAuth integration

## 📥 Download Now

Get PWGen-Rust v1.2 today:

\`\`\`bash
# Snap (Linux)
sudo snap refresh pwgen-rust

# GitHub Releases
wget https://github.com/KiefStudioMA/PWGen-Rust/releases/latest/download/pwgen-v1.2

# Cargo (from source)
cargo install pwgen --version 1.2
\`\`\`

## 🙏 Thank You

This release wouldn't be possible without our amazing community. Your feedback, bug reports, and contributions drive PWGen-Rust forward!

**Join the revolution in password management** - now with 40% less footprint and 100% more security! 🔐

---

*Built with ❤️ and Rust 🦀 by Kief Studio*`
  },
  {
    slug: 'kief-studio-strategic-investment',
    title: 'Kief Studio Announces Strategic Investment in PWGen-Rust Development',
    date: new Date('2025-07-05'),
    authors: ['kief'],
    tags: ['investment', 'announcement', 'strategy', 'enterprise', 'funding', 'development', 'partnership', 'cybersecurity'],
    excerpt: 'Kief Studio announces a strategic investment in PWGen-Rust, bringing enterprise-grade security solutions and long-term development commitment.',
    metaDescription: 'Kief Studio invests in PWGen-Rust development. Enterprise features, professional support, and continued open-source commitment announced.',
    keywords: ['Kief Studio investment', 'PWGen-Rust funding', 'enterprise security', 'open source investment', 'cybersecurity startup', 'password manager enterprise'],
    ogImage: '/img/13.webp',
    socialImage: '/img/KS-Social.png',
    canonicalUrl: 'https://pwgenrust.dev/blog/kief-studio-strategic-investment',
    readingTime: 10,
    content: `# Kief Studio Announces Strategic Investment in PWGen-Rust Development 🚀

![Kief Studio Strategic Investment](/img/13.webp)

Kief Studio is excited to announce our strategic investment in PWGen-Rust, marking a significant milestone in our commitment to delivering cutting-edge cybersecurity solutions.

## Strategic Investment Overview 💼

### Our Commitment to Security Innovation
As a leading AI integration and technology consulting firm, Kief Studio recognizes the critical importance of secure password management in today's digital landscape. Our investment in PWGen-Rust represents a long-term commitment to advancing cybersecurity standards.

### Why PWGen-Rust? 🦀
- **Technical Excellence**: Memory-safe Rust architecture
- **Security First**: Zero-knowledge, client-side encryption
- **Community Driven**: Active open-source development
- **Cross-Platform**: Universal compatibility and performance
- **Innovation**: Cutting-edge approach to password management

## Community Impact and Growth 🌟

PWGen-Rust continues to grow as an open-source password management solution, building a community of security-conscious users and developers who value privacy and performance.

### Community-Driven Development Success
The community has been instrumental in shaping PWGen-Rust:

**Security Enhancements:**
- Argon2 key derivation implementation
- SSH key storage capabilities
- Zero-knowledge architecture refinement

**Feature Implementations:**
- Comprehensive CLI interface for automation
- Cross-platform desktop applications
- 40% binary size optimization in v1.2

**Quality Improvements:**
- Memory leak resolution
- Cross-platform compatibility fixes
- Enhanced user experience

## Enhanced Development Resources 🛠️

### Professional Development Support
Kief Studio's investment enables:

- **Dedicated Engineering**: Full-time development resources
- **Security Auditing**: Regular third-party security assessments
- **Quality Assurance**: Comprehensive testing infrastructure
- **Documentation**: Professional technical writing and guides
- **Community Support**: Enhanced community engagement and support

### Enterprise Feature Development
- **Team Collaboration**: Advanced sharing and permission systems
- **Audit Logging**: Comprehensive compliance and monitoring
- **API Integration**: RESTful APIs for enterprise systems
- **SSO Support**: SAML, OAuth, and LDAP integration

## Technical Roadmap 🛣️

### Q4 2025 Objectives
- **REST API Release**: Complete server-side functionality
- **Browser Extensions**: Chrome, Firefox, and Safari integration
- **Mobile Applications**: Native iOS and Android development
- **Performance Optimization**: Continued efficiency improvements

### 2026 Strategic Vision
- **Enterprise Suite**: Business-grade feature completion
- **Advanced Security**: Enhanced cryptographic implementations
- **Global Expansion**: Multi-language and regional compliance
- **Platform Integration**: Deeper OS and ecosystem integration

## Enterprise Services and Support 🏢

### Professional Consulting
Kief Studio will provide:
- **Implementation Consulting**: Expert deployment guidance
- **Custom Development**: Tailored solutions for specific needs
- **Integration Services**: Enterprise system connectivity
- **Training Programs**: Comprehensive user education

### Flexible Support Options
- **Community Edition**: Continued free access with community support
- **Professional Support**: Business-hours assistance and priority fixes
- **Enterprise Support**: 24/7 dedicated account management
- **Custom Agreements**: Tailored SLAs for organizational requirements

## Security and Development Excellence 🛡️

### Security Commitment
PWGen-Rust is built with security as a primary focus:
- **Open Source Transparency**: All code is publicly auditable
- **Memory Safety**: Rust's ownership model prevents common vulnerabilities
- **Zero-Knowledge Architecture**: Master passwords never leave your device
- **Strong Cryptography**: AES-256-GCM encryption with Argon2 key derivation

### Open Source Commitment
Kief Studio pledges to:
- Maintain PWGen-Rust as open source software
- Contribute all improvements back to the community
- Support community contributors and maintainers
- Ensure transparent and collaborative development

## Looking Forward 🔮

### Shared Vision for the Future
Together with the PWGen-Rust community, we will:
- **Democratize Security**: Make enterprise-grade security accessible
- **Drive Innovation**: Pioneer password management technologies
- **Build Trust**: Establish transparency and security standards
- **Create Value**: Deliver measurable benefits to users worldwide

### Community Engagement
We encourage the community to:
- **Continue Contributing**: Code, documentation, and feedback
- **Stay Engaged**: Participate in discussions and beta testing
- **Spread Awareness**: Share PWGen-Rust with networks
- **Explore Partnerships**: Collaborate on innovative solutions

## Contact and Next Steps 📧

### For Enterprise Inquiries
- **Email**: enterprise@kief.studio
- **Website**: https://kief.studio
- **LinkedIn**: https://www.linkedin.com/company/kief-studio/

### Community Resources
- **GitHub**: https://github.com/KiefStudioMA/PWGen-Rust
- **Issues**: https://github.com/KiefStudioMA/PWGen-Rust/issues
- **Community**: GitHub discussions and support forums

---

**About Kief Studio**  
Kief Studio is a leading AI integration and technology consulting firm specializing in cybersecurity, artificial intelligence, and enterprise software solutions. We partner with organizations to deliver cutting-edge technology solutions that drive business success while maintaining the highest security standards.

---

*Built with ❤️ and Rust 🦀 by Kief Studio*`
  }
];

export const getBlogPostBySlug = (slug) => {
  return blogPosts.find(post => post.slug === slug);
};

export const getBlogPostsByTag = (tag) => {
  return blogPosts.filter(post => post.tags.includes(tag));
};

export const getBlogPostsByAuthor = (authorId) => {
  return blogPosts.filter(post => post.authors.includes(authorId));
};

export const getAllTags = () => {
  const tags = new Set();
  blogPosts.forEach(post => {
    post.tags.forEach(tag => tags.add(tag));
  });
  return Array.from(tags).sort();
};

export const getLatestPosts = (count = 3) => {
  return [...blogPosts]
    .sort((a, b) => b.date - a.date)
    .slice(0, count);
};